import {Component, DestroyRef, inject, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faCalendarClock,
  faEllipsisVertical,
  faMagnifyingGlass,
  faPause,
  faPen,
  faPlay,
  faPlus,
  faSpinnerThird,
  faTrash,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicPopupComponent,
  BicToastService,
  TooltipDirective,
} from '@buildinginformationcloud/bic-ui-components';
import {PageHeaderComponent} from '../../util/design/page-header/page-header.component';
import {EmptyStateComponent} from '../../util/design/empty-state/empty-state.component';
import {MyCheckTabsComponent} from '../shared/my-check-tabs/my-check-tabs.component';
import {CheckRunScheduleView} from '../../../api/model/checkRunScheduleView';
import {CheckRunScheduleApi} from '../../../services/check-run-schedule/check-run-schedule.api';
import {
  BadgeColors,
  describeRecurrence,
  formatBerlin,
  outcomeBadge,
  outcomeLabel,
  pauseReasonLabel,
  sortSchedules,
  statusBadge,
  statusLabel,
  todayBerlinIso,
} from '../../../services/check-run-schedule/check-run-schedule.format';
import {StateConfigService} from '../../../services/state/state-config.service';
import {PermissionService} from '../../../services/permission/permission.service';
import {MyChecksService} from '../my-checks.service';
import {serverErrorMessage} from '../shared/server-error-message';
import {
  CheckRunSchedulePopupComponent,
  ScheduleCheckOption,
  ScheduleConfigureRequest,
} from './check-run-schedule-popup/check-run-schedule-popup.component';

/** Eine Tabellenzeile; alle Anzeigewerte werden einmal beim Laden berechnet (keine Getter im Template). */
interface ScheduleRow {
  schedule: CheckRunScheduleView;
  name: string;
  recurrence: string;
  nextRun: string;
  lastRun: string;
  lastOutcome: string;
  lastOutcomeColors: BadgeColors;
  /** Bericht des letzten erfolgreichen Laufs, sonst `null`. */
  reportInstanceId: string | null;
  status: string;
  statusColors: BadgeColors;
  /** `undefined` statt '' – die Tooltip-Direktive zeigt sonst einen leeren Pfeil. */
  statusTooltip: string | undefined;
  eligibilityIssue: string | undefined;
}

/**
 * Tab „Zeitpläne“ im myCheck-Hub: alle Zeitpläne des Projekts mit nächster und letzter Ausführung.
 *
 * <p>Sichtbar für alle Projektmitglieder; Anlegen, Bearbeiten, Pausieren und Löschen erfordern
 * `PROJECT_CHECK_EXECUTE`. Bei einem Projektwechsel per Route wird die Komponente wiederverwendet,
 * deshalb lädt sie auf `NavigationEnd` neu, sobald sich das Projekt geändert hat.
 */
@Component({
  selector: 'app-check-run-schedules-overview',
  standalone: true,
  imports: [
    FaIconComponent,
    PageHeaderComponent,
    EmptyStateComponent,
    MyCheckTabsComponent,
    BicBadgeComponent,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownElementComponent,
    BicDropdownTriggerDirective,
    BicInputComponent,
    BicPopupComponent,
    TooltipDirective,
    CheckRunSchedulePopupComponent,
  ],
  templateUrl: './check-run-schedules-overview.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./check-run-schedules-overview.component.scss'],
})
export class CheckRunSchedulesOverviewComponent implements OnInit {
  protected readonly faCalendarClock = faCalendarClock;
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly faPause = faPause;
  protected readonly faPen = faPen;
  protected readonly faPlay = faPlay;
  protected readonly faPlus = faPlus;
  protected readonly faSpinnerThird = faSpinnerThird;
  protected readonly faTrash = faTrash;
  protected readonly faTriangleExclamation = faTriangleExclamation;

  protected readonly noPermissionTooltip =
    'Sie haben nicht die erforderlichen Berechtigungen, um Zeitpläne anzulegen.';
  protected readonly noManageTooltip =
    'Sie haben nicht die erforderlichen Berechtigungen, um Zeitpläne zu verwalten.';

  protected loading = true;
  protected loadError = false;
  protected searchQuery = '';
  protected rows: ScheduleRow[] = [];
  protected filteredRows: ScheduleRow[] = [];
  /** Zeitpläne, für die gerade Pausieren/Fortsetzen läuft. */
  protected busyIds = new Set<string>();

  /** Popup offen: `undefined` = geschlossen, `null` = neu anlegen, sonst bearbeiten. */
  protected popupSchedule: CheckRunScheduleView | null | undefined = undefined;
  protected checkOptions: ScheduleCheckOption[] = [];
  protected checkOptionsLoading = false;

  protected deleteTarget: CheckRunScheduleView | null = null;
  protected deleting = false;

  private readonly api = inject(CheckRunScheduleApi);
  private readonly myChecksService = inject(MyChecksService);
  private readonly stateConfigService = inject(StateConfigService);
  private readonly permissionService = inject(PermissionService);
  private readonly toastService = inject(BicToastService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private loadedProjectId: number | null = null;
  /** Laufende Abfrage der Prüfläufe für „Zeitplan anlegen“; endet bei Projektwechsel und Zerstören. */
  private checkOptionsSubscription?: Subscription;

  protected get projectId(): number | undefined {
    return this.stateConfigService.selectedProject?.id;
  }

  protected get canManage(): boolean {
    return this.permissionService.hasProjectPrivilege('PROJECT_CHECK_EXECUTE');
  }

  ngOnInit(): void {
    this.load();
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (this.projectId != null && this.projectId !== this.loadedProjectId) {
        this.popupSchedule = undefined;
        this.deleteTarget = null;
        // Eine noch laufende Prüflauf-Liste gehört zum alten Projekt und darf das Popup nicht öffnen.
        this.checkOptionsSubscription?.unsubscribe();
        this.checkOptionsSubscription = undefined;
        this.checkOptionsLoading = false;
        this.checkOptions = [];
        this.load();
      }
    });
  }

  protected load(): void {
    const projectId = this.projectId;
    if (projectId == null) return;
    // Vor dem Request merken, sonst löst jedes NavigationEnd während des Ladens erneut aus.
    this.loadedProjectId = projectId;
    this.loading = true;
    this.loadError = false;
    this.api.list(projectId).subscribe({
      next: schedules => {
        if (projectId !== this.loadedProjectId) return;
        this.setRows(schedules);
        this.loading = false;
      },
      error: err => {
        if (projectId !== this.loadedProjectId) return;
        console.error('Zeitpläne konnten nicht geladen werden', err);
        this.rows = [];
        this.filteredRows = [];
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  protected onSearch(query: string): void {
    this.searchQuery = query ?? '';
    this.applySearch();
  }

  protected openReport(row: ScheduleRow): void {
    if (!row.reportInstanceId || this.projectId == null) return;
    void this.router.navigate(['projects', this.projectId, 'reports', row.reportInstanceId]);
  }

  protected openCreate(): void {
    const projectId = this.projectId;
    if (!this.canManage || projectId == null) return;
    this.checkOptionsLoading = true;
    this.checkOptionsSubscription?.unsubscribe();
    // Auch beim Verlassen der Seite abbrechen: sonst käme der Fehler-Toast auf der nächsten Seite an.
    this.checkOptionsSubscription = this.myChecksService.getAvailableChecks(projectId).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: result => {
        const scheduled = new Set(this.rows.map(row => row.schedule.definitionId));
        const seen = new Set<string>();
        // Prüfläufe mit Abfrage beim Start bleiben wählbar: das Popup erklärt, warum sie nicht planbar
        // sind, und führt in den Builder, wo die Auswahl festgelegt wird. Planbare stehen oben.
        this.checkOptions = [...result.custom, ...result.shared, ...result.prebuilt]
          .filter(check => check.status === 'ACTIVE' && !scheduled.has(check.id))
          .filter(check => !seen.has(check.id) && !!seen.add(check.id))
          .map((check): ScheduleCheckOption => ({
            id: check.id,
            name: check.name,
            startSelectionStep: check.startSelectionStep,
            editable: !check.readOnly && !check.shared,
          }))
          .sort((a, b) => Number(a.startSelectionStep != null) - Number(b.startSelectionStep != null)
            || a.name.localeCompare(b.name, 'de'));
        this.checkOptionsLoading = false;
        this.popupSchedule = null;
      },
      error: err => {
        this.checkOptionsLoading = false;
        this.toastService.error(serverErrorMessage(err, 'Prüfläufe konnten nicht geladen werden.'));
      },
    });
  }

  protected openEdit(schedule: CheckRunScheduleView): void {
    if (!this.canManage) return;
    this.popupSchedule = schedule;
  }

  protected closePopup(): void {
    this.popupSchedule = undefined;
  }

  /** Aus dem Popup: Prüflauf im Builder öffnen, im Schritt der abgefragten Auswahl. */
  protected openBuilder(request: ScheduleConfigureRequest): void {
    if (this.projectId == null) return;
    this.popupSchedule = undefined;
    void this.router.navigate(['projects', this.projectId, 'check-runs', 'builder', request.definitionId],
      {queryParams: {step: request.step}});
  }

  protected onSaved(): void {
    this.popupSchedule = undefined;
    this.load();
  }

  protected onDeletedInPopup(): void {
    this.popupSchedule = undefined;
    this.load();
  }

  protected pause(schedule: CheckRunScheduleView): void {
    this.runRowAction(schedule, 'pause');
  }

  protected resume(schedule: CheckRunScheduleView): void {
    this.runRowAction(schedule, 'resume');
  }

  protected requestDelete(schedule: CheckRunScheduleView): void {
    if (!this.canManage) return;
    this.deleteTarget = schedule;
  }

  protected cancelDelete(): void {
    if (this.deleting) return;
    this.deleteTarget = null;
  }

  protected confirmDelete(): void {
    const target = this.deleteTarget;
    const projectId = this.projectId;
    if (!target || projectId == null || this.deleting) return;
    this.deleting = true;
    this.api.delete(projectId, target.id).subscribe({
      next: () => {
        this.deleting = false;
        this.deleteTarget = null;
        this.toastService.success('Zeitplan gelöscht');
        this.setRows(this.rows.map(row => row.schedule).filter(s => s.id !== target.id));
      },
      error: err => {
        this.deleting = false;
        this.toastService.error(serverErrorMessage(err, 'Der Zeitplan konnte nicht gelöscht werden.'));
      },
    });
  }

  private runRowAction(schedule: CheckRunScheduleView, action: 'pause' | 'resume'): void {
    const projectId = this.projectId;
    if (!this.canManage || projectId == null || this.busyIds.has(schedule.id)) return;
    this.busyIds.add(schedule.id);
    const request$ = action === 'pause'
      ? this.api.pause(projectId, schedule.id)
      : this.api.resume(projectId, schedule.id);
    request$.subscribe({
      next: updated => {
        this.busyIds.delete(schedule.id);
        this.setRows(this.rows.map(row => row.schedule.id === updated.id ? updated : row.schedule));
        this.toastService.success(action === 'pause' ? 'Zeitplan pausiert' : 'Zeitplan fortgesetzt');
      },
      error: err => {
        this.busyIds.delete(schedule.id);
        this.toastService.error(serverErrorMessage(err, action === 'pause'
          ? 'Der Zeitplan konnte nicht pausiert werden.'
          : 'Der Zeitplan konnte nicht fortgesetzt werden.'));
      },
    });
  }

  private setRows(schedules: CheckRunScheduleView[]): void {
    const today = todayBerlinIso();
    this.rows = sortSchedules(schedules).map(schedule => toRow(schedule, today));
    this.applySearch();
  }

  private applySearch(): void {
    const query = this.searchQuery.trim().toLowerCase();
    this.filteredRows = query
      ? this.rows.filter(row => row.name.toLowerCase().includes(query))
      : this.rows;
  }
}

function toRow(schedule: CheckRunScheduleView, today: string): ScheduleRow {
  const lastOutcome = schedule.lastOutcome ?? null;
  let statusTooltip: string | undefined;
  if (schedule.status === 'PAUSED') {
    statusTooltip = [pauseReasonLabel(schedule.pauseReason), schedule.pauseDetail]
      .filter(text => !!text)
      .join(': ');
  }
  return {
    schedule,
    name: schedule.definitionName ?? 'Prüflauf nicht mehr vorhanden',
    recurrence: describeRecurrence(schedule, today),
    nextRun: formatBerlin(schedule.nextRunAt) || '–',
    lastRun: formatBerlin(schedule.lastRunAt),
    lastOutcome: outcomeLabel(lastOutcome),
    lastOutcomeColors: outcomeBadge(lastOutcome),
    reportInstanceId: lastOutcome === 'COMPLETED' && schedule.lastInstanceId ? schedule.lastInstanceId : null,
    status: statusLabel(schedule.status),
    statusColors: statusBadge(schedule.status),
    statusTooltip: statusTooltip || undefined,
    eligibilityIssue: schedule.eligibilityIssue || undefined,
  };
}
